import React, { useState, useEffect } from 'react';
import { grey, blue,green,red, orange } from '@mui/material/colors';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import DateRangeIcon from '@mui/icons-material/DateRange';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';

import { Box, Stack, Typography,Button,CircularProgress,
    Table,Alert, TableBody, TableContainer,Snackbar,Chip, 
    TableHead, TableRow, Paper, FormGroup} from '@mui/material';
    
import { Tooltip, Zoom, Divider,ClickAwayListener } from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';

import NewAgenda from '../agenda/newAgenda';

import EditarAgenda from './EditarAgenda';
import DeleteAgenda from './DeleteAgenda';

import BASE_URL from '../../config'

import Celda from './Celda';  

const estadosConfig = {
  'programada': { color: blue[700], bgColor:blue[50] }, 
  'cancelada': { color: red[700], bgColor: red[50] },  
  'completada': { color: green[700], bgColor:green[50] }, 
  'default': { color: grey[700], bgColor: grey[50] }
};

const prioridadesConfig = {
  'baja': { color: green[700], bgColor:green[50] }, 
  'media': { color: blue[700], bgColor: blue[50] },
  'alta': { color: orange[700], bgColor: orange[50] },  
  'inmediata': { color: red[700], bgColor:red[50] }, 
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {backgroundColor: grey[100]}
}));

dayjs.extend(isBetween);

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => 
    !['isWithinRange', 'isStart', 'isEnd'].includes(prop),
  })(({outsideCurrentMonth, isWithinRange, isStart, isEnd }) => ({

...(outsideCurrentMonth && {
    color: 'black',
    opacity: 0.6, 
    backgroundColor:grey[100],
    borderRadius:0
  }),

  ...(isWithinRange && {
    backgroundColor: grey[200],
    color: 'black',
    '&:hover, &:focus': {
      backgroundColor: blue[600],
      color: 'white'
    }
  }),

  ...(isStart && {
    backgroundColor: blue[100],
    color: 'black',
    zIndex: 1,
    '&:hover, &:focus': {
      backgroundColor: blue[100],
    }
  }),

  ...(isEnd && {
    backgroundColor: blue[600],
    color: 'white',
    zIndex: 1,
  }),
}));

export default function AgendaRangeFree() {
  // Usuario y permisos: con solo "Ver Agenda" la pantalla es de solo
  // lectura y únicamente muestra las citas propias (como encargado o agente).
  const usuarioActual = (() => {
    try { return JSON.parse(localStorage.getItem('user')) || {}; }
    catch { return {}; }
  })();
  const permisos = usuarioActual?.permisos || [];
  const puedeGestionar = permisos.includes('Gestionar Agenda');

  const esPropia = (item) =>
    item.usuario_id === usuarioActual.id ||
    (Array.isArray(item.lista_agentes_ids) &&
      item.lista_agentes_ids.includes(usuarioActual.id));

  const [agenda, setAgenda] = useState([]);
  const [filtro, setFiltro] = useState('todas');
  const [inicio, setInicio] = useState(null);
  const [fin, setFin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [horaInicio, setHoraInicio] = useState(null);
  const [horaFin, setHoraFin] = useState(null);

  const [openTooltipId, setOpenTooltipId] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, mensaje: '', severity: 'error' });

  // Base sobre la que se calculan filtros y conteos: si no puede gestionar,
  // solo sus propias citas.
  const agendaVisible = puedeGestionar ? agenda : agenda.filter(esPropia);

  const agendaFiltrada = agendaVisible.filter((item) => {
    if (filtro === 'todas') return true;
    // Comparamos en minúsculas para evitar errores de coincidencia
    return item.estado?.toLowerCase() === filtro.toLowerCase();
  });

  const conteos = {
    programada: agendaVisible.filter(i => i.estado?.toLowerCase() === 'programada').length,
    completada: agendaVisible.filter(i => i.estado?.toLowerCase() === 'completada').length,
    cancelada: agendaVisible.filter(i => i.estado?.toLowerCase() === 'cancelada').length,
    todas: agendaVisible.length
  };

  const handleTooltipClose = () => setOpenTooltipId(null);
  const handleTooltipOpen = (id) => setOpenTooltipId(id);

  const fetchAgendas = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/agendas/`);
      if (!res.ok) throw new Error("Error en la red o 404");
      
      const data = await res.json(); 
  
      const listaFinal = Array.isArray(data) ? data : (data.data || []);
      setAgenda(listaFinal);

    } catch (e) {
      console.error("Error cargando agendas:", e);
      setAgenda([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAgendas(); }, []);

  const handleDateChange = (newValue) => {
    if (!inicio || (inicio && fin)) {
      setInicio(newValue);
      setFin(null); // Reinicia el rango
    } else if (newValue.isBefore(inicio)) {
      setInicio(newValue);
      setFin(null);
    } else if (newValue.isSame(inicio, 'day')) {
      setFin(newValue); 
    } else {
      setFin(newValue); // Establece el fin del rango
    }
  };

  const styledbutton={
    backgroundColor:'white',
    color:'black',
    width:{xs:'48%',sm:'auto'},
    textTransform:'none',
    borderRadius: '8px',
    fontWeight:'bold',
    '&:hover, &:focus': {
      backgroundColor:'black',
      color: 'white'
    }
  }

  const renderDay = (props) => {
    const { day, outsideCurrentMonth, ...other } = props;

    const isStart = !outsideCurrentMonth && inicio && day.isSame(inicio, 'day');
    const isEnd = !outsideCurrentMonth && fin && day.isSame(fin, 'day');
    const isWithinRange = !outsideCurrentMonth && inicio && fin && day.isBetween(inicio, fin, 'day', '[]');

    return (
      <CustomPickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        isWithinRange={isWithinRange}
        isStart={isStart}
        isEnd={isEnd}
      />
    );
  };

  const mesesAMostrar = [
    dayjs(), 
  ];

  return (
    <Box sx={{ p: 0, m: 0 }}>
      <Box sx={{display:'flex',flexDirection:{xs:'column',sm:'row'},gap:2,alignItems:{xs:'flex-start',sm:'center'},justifyContent:'space-between'}}>
        <Box sx={{mr:2}}>
          <Typography variant='h6'>Administración de Citas</Typography>
          <Typography variant='body1' color='textSecondary'>Gestiona las citas del personal</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between', width: { xs: '100%', sm: 'auto' } }}>
          {/* Botón Programadas */}
          <Button 
            onClick={() => setFiltro('programada')}
            sx={{ 
              ...styledbutton, 
              backgroundColor: filtro === 'programada' ? blue[600] : 'white',
              color: filtro === 'programada' ? 'white' : 'black',
              '&:hover': { backgroundColor: filtro === 'programada' ? blue[800] : blue[300] }
            }} 
            variant="contained"
          >
            Programadas ({conteos.programada})
          </Button>

          {/* Botón Completadas */}
          <Button 
            onClick={() => setFiltro('completada')}
            sx={{ 
              ...styledbutton, 
              backgroundColor: filtro === 'completada' ? green[600] : 'white',
              color: filtro === 'completada' ? 'white' : 'black',
              '&:hover': { backgroundColor: filtro === 'completada' ? green[800] : green[300] }
            }} 
            variant="contained"
          >
            Completadas ({conteos.completada})
          </Button>

          {/* Botón Canceladas */}
          <Button 
            onClick={() => setFiltro('cancelada')}
            sx={{ 
              ...styledbutton, 
              backgroundColor: filtro === 'cancelada' ? red[600] : 'white',
              color: filtro === 'cancelada' ? 'white' : 'black',
              '&:hover': { backgroundColor: filtro === 'cancelada' ? red[700] : red[200] }
            }} 
            variant="contained"
          >
            Canceladas ({conteos.cancelada})
          </Button>

          {/* Botón Todas */}
          <Button 
            onClick={() => setFiltro('todas')}
            sx={{ 
              ...styledbutton, 
              backgroundColor: filtro === 'todas' ? 'black' : 'white',
              color: filtro === 'todas' ? 'white' : 'black',
              '&:hover': { backgroundColor: filtro === 'todas' ? 'black' : grey[500] }
            }} 
            variant="contained"
          >
            Todas ({conteos.todas})
          </Button>
        </Box>
      </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start',flexDirection: { xs: 'column', md: 'row' }}}>
            {/* Columna izquierda (calendario, horas, crear cita): solo para
                gestores. En modo lectura se oculta y la tabla ocupa todo. */}
            {puedeGestionar && (
            <Box sx={{ flex:1, mt:3, width:'100%' ,height:  { xs: 'auto', md:590 },
                borderRadius: 3,
                bgcolor:'white',
                border:`1px solid ${grey[400]}`}}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <FormGroup sx={{mx:2, mt:3,display:'flex', flexDirection:'row', justifyContent:'space-between',alignItems:'center'}}>
                    <MobileTimePicker sx={{width:'45%'}} label="Hora inicio" 
                      value={horaInicio}
                      onChange={(newValue) => setHoraInicio(newValue)}/> - 
                    <MobileTimePicker 
                      sx={{width:'45%'}}
                      label="Hora salida" 
                      value={horaFin}
                      onChange={(newValue) => setHoraFin(newValue)}
                      />
                </FormGroup>

                <Box sx={{ p: 2 }}>
                  <Paper 
                    elevation={4} 
                    sx={{  
                      border: '1px solid #e0e0e0', 
                      borderRadius: 4, 
                    }}
                  >
                    <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: 'center', px: 2 }}>
                      <Box sx={{ width: 18, height: 18, border:`1px solid black`, borderRadius: '50%' }} />
                      <Typography variant="caption">Día actual</Typography>
                      <Box sx={{ width: 18, height: 18, bgcolor: blue[100], borderRadius: '50%' }} />
                      <Typography variant="caption">Inicio</Typography>
                      <Box sx={{ width: 18, height: 18 , bgcolor: grey[300], borderRadius: '50%', mx:2}}/>
                      <Typography variant="caption">Rango</Typography>
                      <Box sx={{ width: 18, height: 18 , bgcolor: blue[600], borderRadius: '50%', mx:2}} />
                      <Typography variant="caption">Fin</Typography>
                    </Stack>

                    <Box 
                      sx={{ 
                        display: 'flex', 
                        gap: 1,
                        '& .MuiDateCalendar-root': {
                          width: '320px',
                          height: '340px'
                        }
                      }}
                    >
                      {mesesAMostrar.map((mes, index) => (
                        <DateCalendar
                          key={index}
                          value={fin || inicio} 
                          onChange={handleDateChange}
                          referenceDate={mes}
                          slots={{ day: renderDay }}  
                          showDaysOutsideCurrentMonth={true} 
                        />
                      ))}
                    </Box>
                  </Paper>
                </Box>

                {puedeGestionar && (
                  <Box sx={{m:2}}>
                    <NewAgenda datosRango={{
                      inicio: inicio,
                      fin: fin || inicio,
                      horaInicio: horaInicio,
                      horaFin: horaFin
                    }}
                    refresh={fetchAgendas}
                    />
                  </Box>
                )}

              </LocalizationProvider>
            </Box>
            )}

            <TableContainer component={Paper}  sx={{ flex: 4, mt:3, maxHeight: 590,
              borderRadius: 3,
              width:'100%',
              overflow: 'visible',
              border:`1px solid ${grey[400]}`,
              overflowX: 'auto' , '&::-webkit-scrollbar': {
              display: 'none',
              },scrollbarWidth: 'none',msOverflowStyle: 'none'}}>

            <Table  sx={{minWidth: 200}} stickyHeader aria-label="customized table" >
            <TableHead sx={{ bgcolor: grey[100] }}>
              <TableRow >
                <StyledTableCell sx={{fontWeight:'bold'}}>Fecha</StyledTableCell>
                <StyledTableCell sx={{fontWeight:'bold'}}>Cliente</StyledTableCell>
                <StyledTableCell sx={{fontWeight:'bold'}}>Encargado</StyledTableCell>
                <StyledTableCell sx={{fontWeight:'bold'}}>Agentes</StyledTableCell>
                <StyledTableCell sx={{fontWeight:'bold'}}>Estado</StyledTableCell>
                <StyledTableCell sx={{fontWeight:'bold'}}>Prioridad</StyledTableCell>
                <StyledTableCell sx={{fontWeight:'bold'}}>Notas</StyledTableCell>
                <StyledTableCell sx={{fontWeight:'bold'}} align='right' >Acciones</StyledTableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
              // ESTADO: CARGANDO
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 10 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <CircularProgress size={40} sx={{ color: grey[500] }} />
                      <Typography sx={{ mt: 2, color: grey[600] }}>Cargando agendas...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : agendaFiltrada.length > 0 ? (
                agendaFiltrada.map((item) => {
                  return (
                    <TableRow hover key={item.id} 
                        sx={{ 
                          '&:last-child td, &:last-child th': { border: 0 },
                          height: '60px' 
                        }}>

                      {/*COLUMNA FECHA HORA*/}
                      <TableCell variant='body' sx={{width:'auto'}}>
                        <ClickAwayListener onClickAway={handleTooltipClose}>
                          <Box sx={{ display: 'inline-block' }}>
                            <Tooltip
                              PopperProps={{ 
                                disablePortal: false,
                                sx: { zIndex: 9999 } 
                              }}
                              onClose={handleTooltipClose}
                              open={openTooltipId === item.id}
                              disableFocusListener
                              disableHoverListener
                              disableTouchListener
                              TransitionComponent={Zoom}
                              arrow
                              title={
                                <Box sx={{ p: 1.5, minWidth: 120 }}>
                                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    Detalle 
                                  </Typography>
                                  <Divider sx={{ bgcolor: 'white', mb: 1}} />
                                  
                                  <Stack spacing={1.5}>
                                    <Box>
                                      <Typography sx={{display: 'block', fontSize: '0.7rem'}}>
                                        INICIO
                                      </Typography>
                                      <Typography variant="body2">
                                        {dayjs(item.fecha_inicio).format('DD MMM YY')} - {dayjs(`2000-01-01T${item.hora_entrada}`).format('hh:mm A')}
                                      </Typography>
                                    </Box>

                                    <Box>
                                      <Typography  sx={{ display: 'block', fontSize: '0.7rem' }}>
                                        TERMINA
                                      </Typography>
                                      <Typography variant="body2">
                                        {dayjs(item.fecha_fin).format('DD MMM YY')} - {item.hora_salida ? dayjs(`2000-01-01T${item.hora_salida}`).format('hh:mm A') : 'Sin definir'}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </Box>
                              }
                            >
                              <Box 
                                onClick={(e) => {e.stopPropagation();handleTooltipOpen(item.id);}}
                                sx={{ 
                                  display: 'inline-flex', 
                                  alignItems: 'center',
                                  justifyContent:'center', 
                                  gap: 1,
                                  cursor: 'pointer',
                                  padding: 0.5,
                                  borderRadius: '6px',
                                  backgroundColor: openTooltipId === item.id ? grey[300] : 'transparent',
                                  transition: 'all 0.2s ease',
                                  '&:hover': { backgroundColor: grey[300] }
                                }}
                              >
                                <DateRangeIcon/>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ whiteSpace: 'nowrap' }}>
                                  <Typography>
                                    {dayjs(item.fecha_inicio).format('DD MMM YY')}
                                  </Typography>
                                </Stack>
                              </Box>
                            </Tooltip>
                          </Box>
                        </ClickAwayListener>
                      </TableCell>

                      {/*CLIENTE*/}
                      <TableCell variant='body'>{item.nombre_cliente}</TableCell>

                      {/*ENCARGADO*/}
                      <TableCell variant='body'>
                        {item.nombre_encargado || (
                          <span style={{ color: red[700], fontStyle: 'italic' }}>Sin definir</span>
                        )}
                      </TableCell>

                      {/*AGENTES*/}
                      <TableCell><Celda permisosArray={item.lista_agentes}></Celda></TableCell>

                      {/*ESTADO*/}
                      <TableCell variant='body'>
                        {(() => {
                          const estadoActual = item.estado || 'default';
                          const config = estadosConfig[estadoActual] || estadosConfig['default'];
                          
                          return (
                            <Chip 
                              label={item.estado || 'pendiente'} 
                              size="small"
                              sx={{ 
                                fontWeight: 'bold',
                                color: config.color,
                                backgroundColor: config.bgColor,
                                border: `1px solid ${config.color}`,
                                textTransform: 'uppercase',
                                fontSize: '0.65rem',
                                width: '100px',
                                borderRadius: '6px'
                              }} 
                            />
                          );
                        })()}
                      </TableCell>

                      {/*PRIORIDAD*/}
                      <TableCell variant='body'>
                        {(() => {
                          const prioridadActual = item.prioridad || 'default';
                          const config = prioridadesConfig[prioridadActual] || prioridadesConfig['default'];
                          
                          return (
                            <Chip 
                              label={item.prioridad || 'baja'} 
                              size="small"
                              sx={{ 
                                fontWeight: 'bold',
                                color: config.color,
                                backgroundColor: config.bgColor,
                                border: `1px solid ${config.color}`,
                                textTransform: 'uppercase',
                                fontSize: '0.65rem',
                                width: '80px',
                                borderRadius: '6px'
                              }} 
                            />
                          );
                        })()}
                      </TableCell>

                      <TableCell variant='body'>{item.notas}</TableCell>

                      <TableCell align="center" sx={{width:100}}>
                        {puedeGestionar ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                              <EditarAgenda agenda={item} refresh={fetchAgendas} />
                              <DeleteAgenda agenda={item} refresh={fetchAgendas} />
                          </Box>
                        ) : (
                          <Typography variant="caption" color="textSecondary">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
              })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 10}}>
                        <Box sx={{ textAlign: 'center', opacity: 0.5 }}>
                          <EventAvailableIcon sx={{ fontSize: 60, mb: 2, color: grey[400] }} />
                          <Typography variant="h6" color="textSecondary">
                            No se encontraron citas
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {filtro === 'todas' 
                              ? 'Aún no has programado ninguna cita.' 
                              : `No hay tareas "${filtro}" actualmente.`}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                )}
                </TableBody>
            
            </Table>
            </TableContainer>
          </Box>  
        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={4000} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} variant="filled">
            {snackbar.mensaje}
          </Alert>
        </Snackbar>
    </Box>
  );
}